import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getApiBase } from "../../lib/api";

interface ApiOrder {
  id: string;
  total_price: number;
  created_at: string;
  user_id: string | null;

  subtotal_cents?: number | string | null;
  tax_cents?: number | string | null;
  shipping_cents?: number | string | null;
  total_cents?: number | string | null;
}

interface ApiShipping {
  to_name: string;
  to_address: string;
  to_city: string;
  to_state: string;
  to_zip: string;
  to_country: string;
  email?: string | null;
  phone_number?: string | null;
  carrier?: string | null;
  tracking_number?: string | null;
  shipping_rate?: number | string | null;
}

interface ApiItem {
  id: string;
  product_id: string;
  quantity: number;
  price_each: number | string;
  product?: {
    id: string;
    name: string;
    image_url: string;
  } | null;
}

interface ApiResponse {
  order: ApiOrder;
  shipping: ApiShipping;
  items: ApiItem[];
  user?: { full_name: string; email: string } | null;
}

const BRAND = {
  main: "#5c2f2f",
  dark: "#442020",
};

const toNumber = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const formatMoneyFromCents = (cents: number) => (cents / 100).toFixed(2);
const formatMoney = (amount: number) => amount.toFixed(2);

function formatDate(dateISO?: string) {
  if (!dateISO) return "";
  const d = new Date(dateISO);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ReceiptPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [order, setOrder] = useState<ApiOrder | null>(null);
  const [shipping, setShipping] = useState<ApiShipping | null>(null);
  const [items, setItems] = useState<ApiItem[]>([]);
  const [user, setUser] = useState<ApiResponse["user"]>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setError("Missing order ID in URL.");
      return;
    }

    const fetchReceipt = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = new URLSearchParams(location.search).get("token") || "";
        const res = await fetch(
          `${getApiBase()}/api/receipt/${orderId}?token=${encodeURIComponent(token)}`
        );
        const data: ApiResponse | any = await res.json();

        if (!res.ok) throw new Error(data?.error || data?.message || "Failed to fetch receipt");
        if (!data.order || !data.shipping) throw new Error("Incomplete receipt data from server.");

        setOrder(data.order);
        setShipping(data.shipping);
        setItems(data.items || []);
        setUser(data.user ?? null);
      } catch (err: any) {
        setError(err?.message || "Could not load your receipt. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [location.search, orderId]);

  const derived = useMemo(() => {
    // fallback subtotal from items
    const subtotalOld = items.reduce((sum, item) => {
      const priceEach = toNumber(item.price_each);
      const qty = toNumber(item.quantity);
      const line = (Number.isFinite(priceEach) ? priceEach : 0) * (Number.isFinite(qty) ? qty : 0);
      return sum + line;
    }, 0);

    const shippingOld = Number.isFinite(toNumber(shipping?.shipping_rate))
      ? toNumber(shipping?.shipping_rate)
      : 0;

    // fallback tax (if your backend doesn't return tax cents, you can’t reconstruct it reliably)
    const taxOld = 0;

    const totalOld = subtotalOld + shippingOld + taxOld;

    const subtotalCents = toNumber(order?.subtotal_cents);
    const taxCents = toNumber(order?.tax_cents);
    const shippingCents = toNumber(order?.shipping_cents);
    const totalCents = toNumber(order?.total_cents);

    const hasCentsTotals =
      Number.isFinite(subtotalCents) &&
      Number.isFinite(taxCents) &&
      Number.isFinite(shippingCents) &&
      Number.isFinite(totalCents);

    return {
      hasCentsTotals,
      subtotalCents,
      taxCents,
      shippingCents,
      totalCents,
      subtotalOld,
      shippingOld,
      taxOld,
      totalOld,
    };
  }, [items, order, shipping]);

  if (!orderId) {
    return (
      <div className="p-6 max-w-2xl mx-auto bg-white shadow rounded" style={{ color: BRAND.main }}>
        <p className="text-red-500">Missing order ID.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="bg-white shadow rounded-xl p-6 w-full max-w-xl" style={{ color: BRAND.main }}>
          <p>Loading your receipt...</p>
        </div>
      </div>
    );
  }

  if (error || !order || !shipping) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="bg-white shadow rounded-xl p-6 w-full max-w-xl" style={{ color: BRAND.main }}>
          <p className="text-red-500">{error || "Receipt not available. Please contact support."}</p>
        </div>
      </div>
    );
  }

  const shippingRateDisplay = Number.isFinite(toNumber(shipping.shipping_rate))
    ? formatMoney(toNumber(shipping.shipping_rate))
    : null;

  const customerName = user?.full_name || shipping.to_name || "Customer";
  const receiptDate = formatDate(order.created_at);

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6 bg-[#f6f2f2]">
      <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-[260px_1fr] gap-0 shadow-xl rounded-2xl overflow-hidden">
        {/* LEFT SIDEBAR */}
        <aside
          className="p-6 md:p-8 flex flex-col justify-between"
          style={{ backgroundColor: BRAND.main, color: "white" }}
        >
          <div>
            <div className="uppercase tracking-[0.2em] text-xs opacity-90">Order Receipt</div>
            <div className="mt-3 text-2xl font-bold">RECEIPT</div>

            <div className="mt-4 rounded-xl bg-white/10 p-4">
              <div className="text-xs opacity-90">Confirmation #</div>
              <div className="font-semibold break-all">{orderId}</div>
              {receiptDate && (
                <div className="mt-2 text-xs opacity-90">
                  Date: <span className="font-medium">{receiptDate}</span>
                </div>
              )}
            </div>
          </div>

          {/* Optional nav links (purely visual) */}
          <div className="mt-8 space-y-2 text-sm opacity-95">
            <div className="uppercase tracking-wider text-xs opacity-80">Menu</div>
            <button className="text-left w-full hover:opacity-90">Home</button>
            <button className="text-left w-full hover:opacity-90">Orders</button>
            <button className="text-left w-full hover:opacity-90">Support</button>
          </div>
        </aside>

        {/* RIGHT RECEIPT CARD */}
        <main className="bg-white p-6 md:p-10" style={{ color: BRAND.main }}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                Hi {customerName}
              </h1>
              <p className="mt-1 text-sm opacity-80">Thanks for your order!</p>
            </div>

            {/* Small “logo” block (uses your colors) */}
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center font-bold text-white"
              style={{ backgroundColor: BRAND.main }}
              title="Brand"
            >
              10E Beauty
            </div>
          </div>

          {/* Info grid (Deliver to / Payment method-like) */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider opacity-70">Deliver to</div>
              <div className="mt-2 font-semibold">{shipping.to_name}</div>
              <div className="mt-1 text-sm opacity-90">
                {shipping.to_address}
                <br />
                {shipping.to_city}, {shipping.to_state} {shipping.to_zip}
                <br />
                {shipping.to_country}
              </div>

              <div className="mt-3 text-sm space-y-1">
                {shipping.phone_number && (
                  <div>
                    <span className="font-semibold">Phone:</span>{" "}
                    <span className="opacity-90">{shipping.phone_number}</span>
                  </div>
                )}
                {shipping.email && (
                  <div>
                    <span className="font-semibold">Email:</span>{" "}
                    <span className="opacity-90">{shipping.email}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider opacity-70">Shipping Method</div>
              <div className="mt-2 text-sm space-y-1">
                {shipping.carrier ? (
                  <div>
                    <span className="font-semibold">Carrier:</span>{" "}
                    <span className="opacity-90">{shipping.carrier}</span>
                  </div>
                ) : (
                  <div className="opacity-80">Carrier: —</div>
                )}

                {shipping.tracking_number ? (
                  <div>
                    <span className="font-semibold">Tracking:</span>{" "}
                    <span className="opacity-90">{shipping.tracking_number}</span>
                  </div>
                ) : (
                  <div className="opacity-80">Tracking: —</div>
                )}

                {shippingRateDisplay ? (
                  <div>
                    <span className="font-semibold">Rate:</span>{" "}
                    <span className="opacity-90">${shippingRateDisplay}</span>
                  </div>
                ) : (
                  <div className="opacity-80">Rate: —</div>
                )}

              </div>
            </div>
          </div>

          {/* Order details */}
          <div className="mt-10">
            <div className="flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-semibold">Order Details</h2>
              {receiptDate && <div className="text-sm opacity-70">{receiptDate}</div>}
            </div>

            <div className="mt-4 border rounded-xl overflow-hidden">
              <div className="hidden md:grid grid-cols-[72px_1fr_120px_90px_110px] bg-[#fbf7f7] px-4 py-3 text-xs uppercase tracking-wider opacity-80">
                <div>Item</div>
                <div>Product</div>
                <div className="text-right">Price</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Total</div>
              </div>

              {items.map((item) => {
                const priceEach = Number.isFinite(toNumber(item.price_each)) ? toNumber(item.price_each) : 0;
                const qty = Number.isFinite(toNumber(item.quantity)) ? toNumber(item.quantity) : 0;
                const lineTotal = priceEach * qty;

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-[72px_1fr] md:grid-cols-[72px_1fr_120px_90px_110px] gap-3 px-4 py-4 border-t"
                  >
                    <div className="w-[72px] h-[72px] rounded-lg overflow-hidden bg-[#f6f2f2] flex items-center justify-center">
                      {item.product?.image_url ? (
                        <img
                          src={item.product.image_url}
                          alt={item.product?.name || "Product"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-xs opacity-60">No image</div>
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="font-semibold truncate">
                        {item.product?.name || "Product"}
                      </div>

                      {/* mobile-only price/qty */}
                      <div className="md:hidden mt-1 text-sm opacity-90">
                        ${formatMoney(priceEach)} • Qty: {qty}
                      </div>
                    </div>

                    <div className="hidden md:block text-right">${formatMoney(priceEach)}</div>
                    <div className="hidden md:block text-right">{qty}</div>
                    <div className="hidden md:block text-right font-semibold">
                      ${formatMoney(lineTotal)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals (bottom right, like the example) */}
          <div className="mt-8 flex justify-end">
            <div className="w-full max-w-sm border rounded-xl p-5">
              {derived.hasCentsTotals ? (
                <>
                  <div className="flex justify-between text-sm py-1">
                    <span className="opacity-80">Subtotal</span>
                    <span>${formatMoneyFromCents(derived.subtotalCents)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="opacity-80">Tax</span>
                    <span>${formatMoneyFromCents(derived.taxCents)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="opacity-80">Shipping</span>
                    <span>${formatMoneyFromCents(derived.shippingCents)}</span>
                  </div>

                  <div className="my-3 border-t" />

                  <div className="flex justify-between text-base font-bold py-1">
                    <span>Total</span>
                    <span>${formatMoneyFromCents(derived.totalCents)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm py-1">
                    <span className="opacity-80">Subtotal</span>
                    <span>${formatMoney(derived.subtotalOld)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="opacity-80">Tax</span>
                    <span>${formatMoney(derived.taxOld)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="opacity-80">Shipping</span>
                    <span>${formatMoney(derived.shippingOld)}</span>
                  </div>

                  <div className="my-3 border-t" />

                  <div className="flex justify-between text-base font-bold py-1">
                    <span>Total</span>
                    <span>${formatMoney(derived.totalOld)}</span>
                  </div>

                  <p className="mt-3 text-xs opacity-70">
                    Note: Tax totals require the server to return subtotal_cents/tax_cents/shipping_cents/total_cents.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3">
            <button
              onClick={() => window.print()}
              className="px-4 py-2 rounded-lg text-white font-semibold shadow-sm hover:opacity-95"
              style={{ backgroundColor: BRAND.main }}
            >
              Print Receipt
            </button>

            <button
              onClick={() => window.location.assign("/")}
              className="px-4 py-2 rounded-lg font-semibold border hover:bg-[#fbf7f7]"
              style={{ borderColor: BRAND.main, color: BRAND.main }}
            >
              Back to Home
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
